import { queryClient } from '@/shared/lib/query/queryClient';
import { FCM_TOKEN_STORAGE_KEY } from '@/shared/lib/firebase/config';
import { clearTokens } from '@/shared/lib/storage/token';
import { store } from '@/shared/store';
import { clearCredentials } from '@/shared/store/slices/authSlice';

/** Signup / OTP / forgot-password handoff keys (not the authenticated `user` blob). */
const AUTH_HANDOFF_LOCAL_KEYS = [
  'SESSION_ID',
  'SESSION_ID_TWO',
  'otp_type',
  'role',
  'user_email',
  'user_mobile',
  'rememberedEmail',
] as const;

/** When set, AuthGuard navigates here instead of /login after session clear. */
export const POST_LOGOUT_REDIRECT_KEY = 'post_logout_redirect';

function removeLocal(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore quota / private mode
  }
}

/** Drop onboarding form caches keyed by mobile (`user_detail_*`). */
function clearOnboardingSessionStorage() {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key?.startsWith('user_detail_')) keys.push(key);
    }
    keys.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}

/**
 * Local session teardown shared by logout + forced 401.
 * Does not call the logout API or remove the FCM token from the server —
 * callers that still have a valid access token should do that first.
 */
export function clearClientSession() {
  clearTokens();
  store.dispatch(clearCredentials());
  queryClient.clear();
  AUTH_HANDOFF_LOCAL_KEYS.forEach(removeLocal);
  removeLocal(FCM_TOKEN_STORAGE_KEY);
  clearOnboardingSessionStorage();
}

/**
 * Clear session and let AuthGuard soft-navigate to `url` (e.g. signup).
 * Avoids a full page reload from window.location.replace.
 */
export function clearClientSessionAndRedirect(url: string) {
  try {
    sessionStorage.setItem(POST_LOGOUT_REDIRECT_KEY, url);
  } catch {
    // ignore
  }
  clearClientSession();
}
